const DAY_IN_MS = 24 * 60 * 60 * 1000;
const KOREA_TIME_OFFSET_MS = 9 * 60 * 60 * 1000;
const DEFAULT_PRODUCTION_DURATION_DAYS = 28;

function parseDateKey(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return timestamp;
}

export function formatRingPickupDday(value, today = new Date()) {
  const pickupTimestamp = parseDateKey(value);
  if (pickupTimestamp === null) return "";

  const todayTimestamp = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const difference = Math.round(
    (pickupTimestamp - todayTimestamp) / DAY_IN_MS
  );

  if (difference > 0) return `D-${difference}`;
  if (difference < 0) return `D+${Math.abs(difference)}`;
  return "D-Day";
}

export function isRingPickupDue(value, today = new Date()) {
  const pickupTimestamp = parseDateKey(value);
  if (pickupTimestamp === null) return false;

  const koreanToday = new Date(today.getTime() + KOREA_TIME_OFFSET_MS);
  const todayTimestamp = Date.UTC(
    koreanToday.getUTCFullYear(),
    koreanToday.getUTCMonth(),
    koreanToday.getUTCDate()
  );
  return pickupTimestamp <= todayTimestamp;
}

export function calculateRingPickupActiveMilestoneIndex(
  value,
  daysBeforePickup,
  today = new Date()
) {
  const pickupTimestamp = parseDateKey(value);
  if (pickupTimestamp === null || !Array.isArray(daysBeforePickup)) return -1;

  const todayTimestamp = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const remainingDays = Math.round(
    (pickupTimestamp - todayTimestamp) / DAY_IN_MS
  );
  let activeIndex = 0;

  daysBeforePickup.forEach((dayOffset, index) => {
    if (Number.isFinite(dayOffset) && remainingDays <= dayOffset) {
      activeIndex = index;
    }
  });

  return activeIndex;
}

export function calculateRingPickupMilestoneOffsets(
  pickupValue,
  contractValue,
  baselineOffsets
) {
  if (!Array.isArray(baselineOffsets)) return [];

  const normalizedOffsets = baselineOffsets.map((offset) => Number(offset));
  const pickupTimestamp = parseDateKey(pickupValue);
  const contractTimestamp = parseDateKey(contractValue);
  const baselineDuration = normalizedOffsets[0];
  if (
    pickupTimestamp === null ||
    contractTimestamp === null ||
    !Number.isFinite(baselineDuration) ||
    baselineDuration <= 0
  ) {
    return normalizedOffsets;
  }

  const contractDuration = Math.round(
    (pickupTimestamp - contractTimestamp) / DAY_IN_MS
  );
  if (contractDuration < 0) return normalizedOffsets;

  return normalizedOffsets.map((offset) => {
    if (!Number.isFinite(offset)) return offset;
    return Math.min(
      contractDuration,
      Math.max(0, Math.round((contractDuration * offset) / baselineDuration))
    );
  });
}

export function formatRingPickupDate(value) {
  const pickupTimestamp = parseDateKey(value);
  if (pickupTimestamp === null) return "";

  const pickupDate = new Date(pickupTimestamp);
  return `${pickupDate.getUTCFullYear()}년 ${
    pickupDate.getUTCMonth() + 1
  }월 ${pickupDate.getUTCDate()}일`;
}

export function formatRingPickupMilestoneDate(value, daysBeforePickup = 0) {
  const pickupTimestamp = parseDateKey(value);
  const dayOffset = Number(daysBeforePickup);
  if (pickupTimestamp === null || !Number.isFinite(dayOffset)) return "";

  const milestoneDate = new Date(pickupTimestamp - dayOffset * DAY_IN_MS);
  return `${milestoneDate.getUTCMonth() + 1}/${milestoneDate.getUTCDate()}`;
}

export function formatRingPickupMilestoneDay(daysBeforePickup) {
  const dayOffset = Number(daysBeforePickup);
  if (!Number.isFinite(dayOffset)) return "";
  if (dayOffset > 0) return `D-${dayOffset}`;
  if (dayOffset < 0) return `D+${Math.abs(dayOffset)}`;
  return "D-DAY";
}

export function formatRingPickupMilestoneDday(pickupValue, milestoneValue) {
  const pickupTimestamp = parseDateKey(pickupValue);
  const milestoneTimestamp = parseDateKey(milestoneValue);
  if (pickupTimestamp === null || milestoneTimestamp === null) return "";

  const difference = Math.round(
    (pickupTimestamp - milestoneTimestamp) / DAY_IN_MS
  );
  if (difference > 0) return `D-${difference}`;
  if (difference < 0) return `D+${Math.abs(difference)}`;
  return "D-Day";
}

export function calculateCraftsmanProgress(
  pickupAt,
  today = new Date(),
  productionDurationDays = DEFAULT_PRODUCTION_DURATION_DAYS
) {
  const pickupTimestamp = parseDateKey(pickupAt);
  if (pickupTimestamp === null || productionDurationDays <= 0) return 0;

  const todayTimestamp = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const remainingDays = (pickupTimestamp - todayTimestamp) / DAY_IN_MS;
  const progress = 1 - remainingDays / productionDurationDays;
  return Math.min(1, Math.max(0, progress));
}
